/*
 * Header search — Pause Rituals
 * -----------------------------------------------------------------------------
 * Controls the drop-down search panel rendered by snippets/header-search-panel.
 *
 * The panel stays in the DOM and is hidden with visibility + `inert` rather than
 * being added and removed, so the open/close transition can run and so focus can
 * never land inside a closed panel.
 *
 * Shopify's own search does all the real work: <predictive-search> (Dawn) fills
 * the results area, and the <form> posts to routes.search_url for the full
 * results page. Nothing here duplicates that.
 */
class HeaderSearch extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('[data-header-search-panel]');
    this.scrim = this.querySelector('[data-header-search-scrim]');
    this.results = this.querySelector('[data-header-search-results]');
    this.predictiveSearch = this.querySelector('predictive-search');
    this.input = this.querySelector('input[type="search"]');
    this.lastFocused = null;

    if (!this.panel || !this.input) return;

    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.onDocumentPointerdown = this.onDocumentPointerdown.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onViewportChange = this.onViewportChange.bind(this);

    this.querySelectorAll('[data-header-search-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    this.scrim?.addEventListener('click', () => this.close());

    // The toggle lives in .header__icons, outside this element, so it is bound
    // by id rather than by descendant query.
    this.toggles = Array.from(document.querySelectorAll(`[aria-controls="${this.panel.id}"]`));
    this.toggles.forEach((toggle) => {
      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        this.isOpen ? this.close() : this.open(toggle);
      });
    });

    // Dawn re-renders the results markup on every keystroke; recompute the
    // available height whenever the dropdown opens, closes or reloads.
    if (this.predictiveSearch) {
      this.resultsObserver = new MutationObserver(() => this.setResultsHeight());
      this.resultsObserver.observe(this.predictiveSearch, {
        attributes: true,
        attributeFilter: ['open', 'loading', 'results'],
      });
    }

    this.setClosedState();
  }

  disconnectedCallback() {
    this.resultsObserver?.disconnect();
    this.teardownGlobalListeners();
    this.unlockScroll();
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  open(toggle) {
    if (this.isOpen) return;

    this.lastFocused = toggle || document.activeElement;
    this.panel.removeAttribute('inert');
    this.panel.setAttribute('aria-hidden', 'false');
    this.classList.add('is-open');
    this.toggles.forEach((button) => button.setAttribute('aria-expanded', 'true'));

    this.lockScroll();
    this.positionScrim();
    this.setResultsHeight();
    this.setupGlobalListeners();

    // Wait a frame: focusing an element that is still `visibility: hidden` is
    // a no-op in Safari.
    requestAnimationFrame(() => {
      this.input.focus();
      this.input.select();
    });
  }

  close(returnFocus = true) {
    if (!this.isOpen) return;

    this.classList.remove('is-open');
    this.setClosedState();
    this.toggles.forEach((button) => button.setAttribute('aria-expanded', 'false'));

    // Dismiss Dawn's predictive dropdown too, so reopening starts clean.
    this.predictiveSearch?.close?.();

    this.unlockScroll();
    this.teardownGlobalListeners();

    if (returnFocus && this.lastFocused && document.contains(this.lastFocused)) {
      this.lastFocused.focus();
    }
    this.lastFocused = null;
  }

  setClosedState() {
    this.panel.setAttribute('inert', '');
    this.panel.setAttribute('aria-hidden', 'true');
  }

  /* ---- Global listeners, only bound while open --------------------------- */

  setupGlobalListeners() {
    document.addEventListener('keydown', this.onDocumentKeydown);
    document.addEventListener('pointerdown', this.onDocumentPointerdown);
    this.addEventListener('focusout', this.onFocusOut);
    window.addEventListener('resize', this.onViewportChange);
    window.addEventListener('scroll', this.onViewportChange, { passive: true });
  }

  teardownGlobalListeners() {
    document.removeEventListener('keydown', this.onDocumentKeydown);
    document.removeEventListener('pointerdown', this.onDocumentPointerdown);
    this.removeEventListener('focusout', this.onFocusOut);
    window.removeEventListener('resize', this.onViewportChange);
    window.removeEventListener('scroll', this.onViewportChange);
  }

  onDocumentKeydown(event) {
    if (event.key !== 'Escape') return;
    event.stopPropagation();
    this.close();
  }

  onDocumentPointerdown(event) {
    if (this.contains(event.target)) return;
    if (this.toggles.some((toggle) => toggle.contains(event.target))) return;
    this.close(false);
  }

  onFocusOut(event) {
    // relatedTarget is null on a mouse click into empty space; pointerdown
    // already covers that case, and closing here would fight the scrim.
    const next = event.relatedTarget;
    if (!next) return;
    if (this.contains(next)) return;
    if (this.toggles.some((toggle) => toggle.contains(next))) return;
    this.close(false);
  }

  onViewportChange() {
    if (this.viewportFrame) return;
    this.viewportFrame = requestAnimationFrame(() => {
      this.viewportFrame = null;
      this.positionScrim();
      this.setResultsHeight();
    });
  }

  /* ---- Measurements ------------------------------------------------------ */

  /* Start the scrim at the panel's bottom edge so the header stays visible. */
  positionScrim() {
    if (!this.scrim) return;
    const bottom = this.panel.getBoundingClientRect().bottom;
    this.scrim.style.top = `${Math.max(0, Math.round(bottom))}px`;
  }

  /*
   * Cap the results list at the space left below it, so a long list scrolls
   * inside the panel instead of running off the bottom of the screen.
   */
  setResultsHeight() {
    if (!this.results) return;
    const top = this.results.getBoundingClientRect().top;
    const available = window.innerHeight - top - 24;
    this.panel.style.setProperty('--hs-results-max', `${Math.max(120, Math.round(available))}px`);
    // The panel just changed height, so the scrim needs to follow it.
    this.positionScrim();
  }

  /* ---- Scroll lock ------------------------------------------------------- */

  lockScroll() {
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.setProperty('--hs-scrollbar-width', `${scrollbar}px`);
    document.body.classList.add('header-search-open');
  }

  unlockScroll() {
    document.body.classList.remove('header-search-open');
    document.body.style.removeProperty('--hs-scrollbar-width');
  }
}

if (!customElements.get('header-search')) {
  customElements.define('header-search', HeaderSearch);
}
